import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { clientMap } from "@/lib/hooks";
import { UserAvatar } from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Search, X } from "lucide-react";
import { format, isToday, isYesterday, parseISO } from "date-fns";
import { pt } from "date-fns/locale";

function dayLabel(key) {
  if (!key) return "Sem data";
  const day = parseISO(key);
  if (isToday(day)) return "Hoje";
  if (isYesterday(day)) return "Ontem";
  return format(day, "EEEE, d 'de' MMMM", { locale: pt });
}

/**
 * Histórico das tarefas concluídas, à parte do quadro (que só mostra as de
 * hoje). Filtra por dia e pesquisa pelo nome do cliente ou da tarefa.
 */
export function CompletedTasksPanel({ team, personId, users, clients, onOpen }) {
  const [date, setDate] = useState("");
  const [search, setSearch] = useState("");
  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["tasks", "completed", team ? "team" : "mine", date],
    queryFn: async () => (await api.get("/tasks/completed", {
      params: { ...(date ? { date } : {}), ...(team ? { team: true } : {}) },
    })).data,
  });
  const cm = useMemo(() => clientMap(clients), [clients]);

  const term = search.trim().toLowerCase();
  const visible = tasks.filter((t) => {
    if (team && personId !== "all" && !(t.assigneeIds || []).includes(personId)) return false;
    if (!term) return true;
    const clientName = cm[t.clientId]?.name || "";
    return clientName.toLowerCase().includes(term) || t.title.toLowerCase().includes(term);
  });

  // Já vêm ordenadas da mais recente para a mais antiga: agrupa por dia.
  const groups = [];
  for (const task of visible) {
    const key = task.completedAt ? format(parseISO(task.completedAt), "yyyy-MM-dd") : "";
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(task);
    else groups.push({ key, items: [task] });
  }

  return (
    <div className="space-y-4" data-testid="completed-tasks">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-9 w-auto"
          aria-label="Dia"
          data-testid="completed-date"
        />
        {date && (
          <Button variant="ghost" size="sm" onClick={() => setDate("")} className="text-xs text-muted-foreground">
            <X className="h-3.5 w-3.5 mr-1" /> Todos os dias
          </Button>
        )}
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pesquisar por cliente ou tarefa"
            className="h-9 pl-9"
            data-testid="completed-search"
          />
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">A carregar...</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {date || term ? "Nenhuma tarefa concluída com estes filtros." : "Ainda não há tarefas concluídas."}
        </p>
      ) : (
        groups.map((group) => (
          <div key={group.key || "sem-data"}>
            <div className="mb-2 flex items-center gap-2">
              <h4 className="font-poppins text-xs font-semibold uppercase tracking-wide text-muted-foreground">{dayLabel(group.key)}</h4>
              <span className="text-xs text-muted-foreground">{group.items.length}</span>
            </div>
            <div className="space-y-1.5">
              {group.items.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onOpen(t)}
                  className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 text-left transition-colors hover:border-primary/40"
                  data-testid={`completed-task-${t.id}`}
                >
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                  <span className="min-w-0 flex-1 truncate font-roboto text-sm">{t.title}</span>
                  {t.clientId && cm[t.clientId] && (
                    <Badge variant="secondary" className="hidden shrink-0 rounded-full text-[10px] sm:inline-flex">{cm[t.clientId].name}</Badge>
                  )}
                  <div className="flex shrink-0 -space-x-1.5">
                    {(t.assigneeIds || []).slice(0, 3).map((id) => users[id] && <UserAvatar key={id} user={users[id]} size="h-5 w-5 ring-1 ring-card" />)}
                  </div>
                  {t.completedAt && (
                    <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{format(parseISO(t.completedAt), "HH:mm")}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
